import { memo, useCallback, useContext } from 'react';
import styled, { type CSSObject } from '@emotion/styled';

import type { ThemeProps } from '@emotion/react';

import { colorAlpha } from '../../../../lib/colorAlpha';
import { useCurrentRoute } from '../../../../lib/useCurrentRoute';
import useTracking from '../../../../lib/useTracking';
import MobileNavItem from './Item';

import type { NavProps } from '../Desktop/Nav';
import { WWContext } from '../../../WWContext';
import { useActiveSinglePageSectionContext } from '../../../../context/ActiveSinglePageSectionContext';

interface ListWrapProps {
  inPageVersion: boolean;
}

const ListWrap = styled('ul')`
  display: flex;
  flex-direction: column;
  align-items: center;
  list-style: none;
  margin: 0;
  padding: 0;

  ${(props: ListWrapProps & ThemeProps): string =>
    props.inPageVersion
      ? `border-top: 1px solid ${colorAlpha(
          props.theme.fonts.s5.color,
          '45%'
        )};`
      : ''}

  ${(props: ThemeProps): CSSObject | undefined =>
    props.theme.components.Nav?.Mobile}
`;

export const onTracking = (
  selection: string,
  trackRsvp: (selection: string, trackArea?: string | undefined) => void,
  trackGuest: (selection: string, trackArea?: string | undefined) => void
) => {
  if (selection === 'rsvp') {
    trackRsvp(selection);
  }

  trackGuest(selection);
};

export interface MobileNavListProps extends NavProps {
  userDecisionArea: string;
  inPageVersion?: boolean | undefined;
  setMobileNavOpen?: ((open: boolean) => void) | undefined;
}

function MobileNavList({
  inPageVersion = false,
  pages,
  setMobileNavOpen,
  userDecisionArea,
}: MobileNavListProps) {
  const currentRoute = useCurrentRoute();
  const { isSinglePageLayout } = useContext(WWContext);
  const { activeSectionId: activeSinglePageSectionId } =
    useActiveSinglePageSectionContext();

  const trackGuest = useTracking('guest', currentRoute, userDecisionArea);

  const trackRsvp = useTracking('rsvp', currentRoute, userDecisionArea);

  const handleTracking = useCallback(
    (selection: string) => {
      onTracking(selection, trackRsvp, trackGuest);
    },
    [trackGuest, trackRsvp]
  );

  if (!pages?.length) {
    return null;
  }

  return (
    <ListWrap inPageVersion={inPageVersion}>
      {pages.map((page) => {
        const shouldScrollToTopOnNavigate = !isSinglePageLayout;
        return (
          <MobileNavItem
            key={page.id}
            page={page}
            inPageVersion={inPageVersion}
            setMobileNavOpen={setMobileNavOpen}
            handleTracking={handleTracking}
            shouldScrollToTopOnNavigate={shouldScrollToTopOnNavigate}
            activeSinglePageSectionId={activeSinglePageSectionId}
          />
        );
      })}
    </ListWrap>
  );
}

export default memo(MobileNavList);
